import { api } from "@accredo/fe-shared/services/dataService.js";

/**
 * The customer an internal user (sysadmin, accredo) narrowed the portal to:
 * `{ id, name, member_count }`. Kept in sessionStorage so it survives a
 * reload; authService drops it together with the session on logout.
 */
export const CUSTOMER_SELECTION_KEY = "customer_selection";

export const readCustomerSelection = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CUSTOMER_SELECTION_KEY));
  } catch {
    return null;
  }
};

export const writeCustomerSelection = (selection) => {
  if (selection) {
    sessionStorage.setItem(CUSTOMER_SELECTION_KEY, JSON.stringify(selection));
  } else {
    sessionStorage.removeItem(CUSTOMER_SELECTION_KEY);
  }
};

/**
 * The customers this browser selected most recently, newest first, offered by
 * the picker before anything is typed. A convenience only, so it lives in
 * localStorage and survives logout.
 */
export const RECENT_CUSTOMERS_KEY = "recent_customer_selections";
const RECENT_CUSTOMERS_LIMIT = 5;

export const readRecentCustomers = () => {
  try {
    const recent = JSON.parse(localStorage.getItem(RECENT_CUSTOMERS_KEY));
    return Array.isArray(recent) ? recent : [];
  } catch {
    return [];
  }
};

export const rememberRecentCustomer = (selection) => {
  const recent = [
    selection,
    ...readRecentCustomers().filter((entry) => entry.id !== selection.id),
  ].slice(0, RECENT_CUSTOMERS_LIMIT);

  try {
    localStorage.setItem(RECENT_CUSTOMERS_KEY, JSON.stringify(recent));
  } catch {
    // Storage blocked or full: the picker simply has no recent entries.
  }
};

// The endpoints that know the kunde_scope filter. Any other query-builder
// endpoint answers an unknown filter with a 400, so the filter is only ever
// added to these.
const SCOPED_ENDPOINTS = [
  /^data\/vertrags$/,
  /^data\/vertrags\/(\d+|sparte-counts)$/,
  /^data\/objekts$/,
  /^data\/documents$/,
  /^data\/documents\/(bulk-download|type-counts)$/,
  /^portal\/search$/,
  /^portal\/vertrags\/\d+\/claims-summary$/,
];

const isScopedEndpoint = (url) => {
  const path = String(url ?? "")
    .split("?")[0]
    .replace(/^\/+/, "");

  return SCOPED_ENDPOINTS.some((pattern) => pattern.test(path));
};

/**
 * Adds `filter[kunde_scope]=<kundes.id>` to the requests above while a customer is
 * selected, merged into whatever `filter` params the request already has.
 * Only the Kundenportal app registers this; the Schadenportal never does.
 *
 * @returns {() => void} Removes the interceptor again.
 */
export function registerCustomerScopeInterceptor(instance = api) {
  const id = instance.interceptors.request.use((config) => {
    const selection = readCustomerSelection();

    if (selection?.id && isScopedEndpoint(config.url)) {
      config.params = {
        ...config.params,
        filter: { ...config.params?.filter, kunde_scope: selection.id },
      };
    }

    return config;
  });

  return () => instance.interceptors.request.eject(id);
}
